import { useMemo } from 'react';
import { AppRoutes } from '@/app/routes';

const collectRouteStrings = (routeNode: unknown): string[] => {
  if (typeof routeNode === 'string') {
    return [routeNode];
  }

  if (!routeNode || typeof routeNode !== 'object') {
    return [];
  }

  return Object.values(routeNode).flatMap((value) => {
    if (typeof value === 'string') {
      return [value];
    }

    if (typeof value === 'function') {
      return [];
    }

    return collectRouteStrings(value);
  });
};

/**
 * Returns a validator that accepts any URL whose pathname (i.e. path before
 * any `?` query string) either:
 *   1. Exactly matches a known static route string, OR
 *   2. Starts with a known static route prefix followed by `/` or end-of-string
 *      (covers dynamic segments such as `/admin/clients/25653?tab=funding`).
 *
 * This allows breadcrumb entries built with dynamic IDs and/or query params to
 * render as clickable `<Link>` elements rather than plain text.
 */
export const useValidRouteUrl = () => {
  const routeStrings = useMemo(() => collectRouteStrings(AppRoutes), []);

  return (url: string | undefined): url is string => {
    if (typeof url !== 'string') return false;

    // Strip query string to obtain just the pathname
    const pathname = url.split('?')[0];

    return routeStrings.some(
      (route) =>
        pathname === route ||
        pathname.startsWith(route + '/'),
    );
  };
};
